/**
 * The Chats pin (Part 09 decisions 3 and 6, mockup `chatsPin`): the search
 * hub's own chat list, its rows and their menu (Stop generating, Rename,
 * Archive), at most six over Running, Today and Earlier. The project goes
 * to the server, so the rows shown are the newest that exist there; null
 * lists every project's. The wall's search goes to the hub's server search
 * and marks the matches as the hub does. A row opens the chat as the hub
 * does; "All chats" opens the hub on Chats. The list shows what fits and
 * fades at the tile's foot; the wheel scrolls the wall.
 */
import { useMemo, useState } from "react";
import type { DurableConversation } from "@/api/conversations";
import { ChatRenameDialog } from "@/components/panels/search-modal/chat-rename-dialog";
import { chatResult } from "@/components/panels/search-modal/hub-results";
import { chatSections } from "@/components/panels/search-modal/hub-sections";
import type {
  HubItem,
  HubSection,
} from "@/components/panels/search-modal/hub-types";
import { ResultRow } from "@/components/panels/search-modal/result-row";
import {
  openHubChat,
  useHubChats,
} from "@/components/panels/search-modal/use-hub-chats";
import { FrameCalmTitle } from "@/components/shapes/shared/frame-calm-title";
import { SurfaceFade } from "@/components/ui";
import { useT } from "@/i18n";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";
import { wallMessages } from "@/i18n/messages/wall";
import { openGlobalSearch } from "@/lib/global-search";
import { WallPinTile } from "./wall-pin-tile";
import { useWallTileQuery } from "@/components/shapes/shared/wall-tile-query-context";

const CHAT_ROWS = 6;

/** Opening a row from the tile has no popover to close. */
function keepOpen(): void {}

function chatOf(item: HubItem): DurableConversation | null {
  return item.type === "result" && item.result.kind === "chat"
    ? item.result.chat
    : null;
}

/** The sections cut to `limit` rows in order; emptied sections go. */
function firstRows(sections: HubSection[], limit: number): HubSection[] {
  let left = limit;
  return sections.flatMap((section) => {
    const items = section.items.slice(0, Math.max(left, 0));
    left -= items.length;
    return items.length > 0 ? [{ ...section, items }] : [];
  });
}

export function WallChatsTile({ projectId }: { projectId: string | null }) {
  const t = useT(wallMessages);
  const tSearch = useT(panelsSearchMessages);
  const query = useWallTileQuery();
  const chats = useHubChats({ query, projectId, archived: false });
  const [renaming, setRenaming] = useState<DurableConversation | null>(null);
  const sections = useMemo(
    () =>
      firstRows(
        chatSections(
          chats.active.map(chatResult),
          query,
          projectId,
          Date.now(),
        ),
        CHAT_ROWS,
      ),
    [chats.active, projectId, query],
  );
  const count = `${chats.active.length}${chats.hasMore ? "+" : ""}`;

  return (
    <WallPinTile pin="chats" label={tSearch("chats")}>
      <FrameCalmTitle
        title={tSearch("chats")}
        note={chats.loading ? undefined : count}
        action={{
          label: t("allChats"),
          onClick: () => openGlobalSearch({ chip: "chats", projectId }),
          testId: "wall-pin-all-chats",
        }}
        data-testid="wall-chats-top-row"
      />
      <div
        className="relative min-h-0 flex-1 overflow-hidden px-[0.8em] pb-[0.8em] pt-[0.5em]"
        data-testid="wall-chats-list"
      >
        {sections.map((section) => (
          <section key={section.id} data-testid={`wall-chats-${section.id}`}>
            <h5 className="mx-[0.4em] mb-[0.35em] mt-[0.6em] text-[0.78em] font-medium tracking-[0.02em] text-muted">
              {section.title}
            </h5>
            {section.items.map((item) => {
              const chat = chatOf(item);
              return chat && item.type === "result" ? (
                <ResultRow
                  key={item.key}
                  result={item.result}
                  query={query}
                  selected={false}
                  onOpen={() => openHubChat(chat, keepOpen)}
                  menu={chats.menu(chat, setRenaming)}
                />
              ) : null;
            })}
          </section>
        ))}
        {sections.length === 0 && !chats.loading ? (
          <p
            className="mx-[0.4em] mt-[0.6em] text-appkit-body text-muted"
            data-testid="wall-chats-empty"
          >
            {chats.error ??
              (query ? t("noChatsMatch", { query }) : t("noChatsYet"))}
          </p>
        ) : null}
        <SurfaceFade />
      </div>
      {renaming ? (
        <ChatRenameDialog
          chat={renaming}
          onRename={chats.rename}
          onClose={() => setRenaming(null)}
        />
      ) : null}
    </WallPinTile>
  );
}
